var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback } from 'react';
import { Contract } from 'ethers';
import { t } from 'i18next';
import { getContract } from '@sovryn/contracts';
import { SupportedTokens } from '@sovryn/contracts';
import { TransactionType, } from '../../../3_organisms/TransactionStepDialog/TransactionStepDialog.types';
import { isTransactionRequest } from '../../../3_organisms/TransactionStepDialog/helpers';
import { GAS_LIMIT } from '../../../../constants/gasLimits';
import { getTokenDisplayName } from '../../../../constants/tokens';
import { useTransactionContext } from '../../../../contexts/TransactionContext';
import { useAccount } from '../../../../hooks/useAccount';
import { translations } from '../../../../locales/i18n';
import { getRskChainId } from '../../../../utils/chain';
import { loadLiquity } from '../../../../utils/liquity';
import { toWei } from '../../../../utils/math';
import { permitHandler, preparePermitTransaction, UNSIGNED_PERMIT, } from '../../../../utils/transactions';
import { adjustTrove, openTrove } from '../utils/trove-manager';
const baseTranslationPath = translations.zeroPage.tx;
const getAdjustTroveTexts = (value) => {
    const isAdjustingDebt = (value.borrow && value.borrow !== '0') ||
        (value.repay && value.repay !== '0');
    const isAdjustingCollateral = (value.depositCollateral && value.depositCollateral !== '0') ||
        (value.withdrawCollateral && value.withdrawCollateral !== '0');
    if (value.repay && !isAdjustingCollateral) {
        return {
            dialogTitle: t(baseTranslationPath.repayTitle),
            transactionTitle: t(baseTranslationPath.repay, {
                asset: getTokenDisplayName(value.token),
            }),
        };
    }
    if (value.borrow && !isAdjustingCollateral) {
        return {
            dialogTitle: t(baseTranslationPath.borrowTitle),
            transactionTitle: t(baseTranslationPath.borrow, {
                asset: getTokenDisplayName(value.token),
            }),
        };
    }
    if (value.depositCollateral && !isAdjustingDebt) {
        return {
            dialogTitle: t(baseTranslationPath.addCollateralTitle),
            transactionTitle: t(baseTranslationPath.addCollateral),
        };
    }
    if (value.withdrawCollateral && !isAdjustingDebt) {
        return {
            dialogTitle: t(baseTranslationPath.withdrawCollateralTitle),
            transactionTitle: t(baseTranslationPath.withdrawCollateral),
        };
    }
    return {
        dialogTitle: t(baseTranslationPath.adjustTitle),
        transactionTitle: t(baseTranslationPath.adjust),
    };
};
export const useHandleTrove = (hasLoc, callbacks) => {
    const { signer, account } = useAccount();
    const { setTransactions, setIsOpen, setTitle } = useTransactionContext();
    const handleTroveSubmit = useCallback((value) => __awaiter(void 0, void 0, void 0, function* () {
        if (signer) {
            const { address, abi } = yield getContract('borrowerOperations', 'zero', getRskChainId());
            const contract = new Contract(address, abi, signer);
            if (hasLoc) {
                const { dialogTitle, transactionTitle } = getAdjustTroveTexts(value);
                const params = {};
                if (value.borrow) {
                    params.borrowZUSD = value.borrow;
                }
                if (value.repay) {
                    params.repayZUSD = value.repay;
                }
                if (value.depositCollateral) {
                    params.depositCollateral = value.depositCollateral;
                }
                if (value.withdrawCollateral) {
                    params.withdrawCollateral = value.withdrawCollateral;
                }
                const isDllr = value.token === SupportedTokens.dllr;
                const transactions = [];
                if (isDllr && params.repayZUSD) {
                    transactions.push(yield preparePermitTransaction({
                        signer,
                        token: SupportedTokens.dllr,
                        spender: address,
                        value: toWei(params.repayZUSD.toString()).toString(),
                    }));
                }
                const adjustedTrove = yield adjustTrove(value.token, account, params, value.maxOriginationFeeRate);
                transactions.push({
                    title: transactionTitle,
                    request: {
                        type: TransactionType.signTransaction,
                        contract,
                        fnName: adjustedTrove.fn,
                        args: isDllr
                            ? [...adjustedTrove.args, UNSIGNED_PERMIT]
                            : adjustedTrove.args,
                        value: adjustedTrove.value,
                        gasLimit: GAS_LIMIT.ADJUST_TROVE,
                    },
                    onComplete: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveAdjusted,
                    updateHandler: permitHandler((req, res) => {
                        if (isTransactionRequest(req) && isDllr) {
                            req.args = [...adjustedTrove.args, res];
                        }
                        return req;
                    }),
                });
                setTransactions(transactions);
                setIsOpen(true);
                setTitle(dialogTitle);
            }
            else {
                const openedTrove = yield openTrove(value.token, {
                    borrowZUSD: value.borrow || '0',
                    depositCollateral: value.depositCollateral || '0',
                }, value.maxOriginationFeeRate);
                setTransactions([
                    {
                        title: t(baseTranslationPath.open),
                        request: {
                            type: TransactionType.signTransaction,
                            contract,
                            fnName: openedTrove.fn,
                            args: openedTrove.args,
                            value: openedTrove.value,
                            gasLimit: GAS_LIMIT.OPEN_TROVE,
                        },
                        onComplete: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveOpened,
                    },
                ]);
                setIsOpen(true);
                setTitle(t(baseTranslationPath.openTitle));
            }
        }
    }), [
        account,
        callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveAdjusted,
        callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveOpened,
        hasLoc,
        setIsOpen,
        setTitle,
        setTransactions,
        signer,
    ]);
    const handleTroveClose = useCallback((token) => __awaiter(void 0, void 0, void 0, function* () {
        if (signer) {
            const { address, abi } = yield getContract('borrowerOperations', 'zero', getRskChainId());
            const contract = new Contract(address, abi, signer);
            const isDllr = token === SupportedTokens.dllr;
            if (isDllr) {
                const { liquity } = yield loadLiquity();
                const value = yield liquity
                    .getTrove(account)
                    .then(trove => toWei(trove.netDebt.toString()).toString());
                setTransactions([
                    yield preparePermitTransaction({
                        signer,
                        token: SupportedTokens.dllr,
                        spender: address,
                        value,
                    }),
                    {
                        title: t(baseTranslationPath.close),
                        request: {
                            type: TransactionType.signTransaction,
                            contract,
                            fnName: 'closeNueTrove',
                            args: [],
                            gasLimit: GAS_LIMIT.CLOSE_DLLR_TROVE,
                        },
                        onComplete: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveClosed,
                        updateHandler: permitHandler((req, res) => {
                            if (isTransactionRequest(req)) {
                                req.args = [res];
                            }
                            return req;
                        }),
                    },
                ]);
            }
            else if (token === SupportedTokens.zusd) {
                setTransactions([
                    {
                        title: t(baseTranslationPath.close),
                        request: {
                            type: TransactionType.signTransaction,
                            contract,
                            fnName: 'closeTrove',
                            args: [],
                            gasLimit: GAS_LIMIT.CLOSE_TROVE,
                        },
                        onComplete: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveClosed,
                    },
                ]);
            }
            else {
                throw new Error('Unsupported token');
            }
            setIsOpen(true);
            setTitle(t(baseTranslationPath.closeTitle));
        }
    }), [
        account,
        callbacks === null || callbacks === void 0 ? void 0 : callbacks.onTroveClosed,
        setIsOpen,
        setTitle,
        setTransactions,
        signer,
    ]);
    return { handleTroveSubmit, handleTroveClose };
};
